import type { AppConfig } from '../config'
import { formatLocalDateTime, LOCALE } from '../lib/days'
import { asSentence, formatWait } from '../lib/format'
import { cleanPhoto } from '../lib/photo'
import { APP_VERSION, PLATFORM } from '../lib/version'
import type {
  AiRecipe,
  AiReportRequest,
  DeleteAccountResult,
  ExportResult,
  Food,
  FoodKind,
  ImportRequest,
  ImportResult,
  InAppMessage,
  ItemType,
  LatestExport,
  PlateRequest,
  PlateResult,
  ProgramAiRequest,
  ProgramAiResult,
  PublishedFile,
  PullPage,
  PushDeviceRequest,
  PushResult,
  RecipeImport,
  RemoteConfig,
  SyncItem,
  UploadKind,
  UploadTicket,
  ViewUrls,
} from './types'

export const MAX_PUSH_ITEMS = 100

export class ApiError extends Error {
  readonly status: number
  /** The parsed error body, when there was one: lets a caller read fields beyond [message] (e.g. suspension.py's
   * `{suspended, deleteAt, appealEmail}` on a 403). */
  readonly payload: unknown

  constructor(status: number, message: string, payload?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.payload = payload
  }
}

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>

/** Where the client gets access tokens. The signed-in session (AuthManager) implements it. */
export interface TokenProvider {
  /** An access token with time left, refreshed first when it is about to expire. */
  getAccessToken(): Promise<string>
  /**
   * A newer token than [rejected], which the API just answered 401 to. Throws when the session cannot
   * be refreshed; when the refresh endpoint says the session is over, the user is already signed out.
   */
  refreshAccessToken(rejected: string): Promise<string>
  /** Ends the session because the API rejected even a freshly refreshed token. */
  signOut(): Promise<void>
  /** Called when any request hits a suspended-account 403 (docs/plan/api.md#moderation), so the app can replace
   * itself with the suspension notice instead of each screen failing on its own. Optional: only AuthManager
   * implements it; a test double that doesn't care about suspension can omit it. */
  reportSuspension?(suspension: Suspension): void
}

/** Reads a JSON response body; an empty or non-JSON body reads as null. */
export async function readJsonBody(response: Response): Promise<unknown> {
  const text = await response.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

/** The people-facing message from an `{"error": "..."}` body, or a generic one naming the status. */
export function errorMessage(payload: unknown, status: number): string {
  return payload && typeof payload === 'object' && 'error' in payload && typeof payload.error === 'string'
    ? payload.error
    : `request failed (HTTP ${status})`
}

function errorBody(error: unknown, status?: number): Record<string, unknown> | null {
  if (!(error instanceof ApiError) || (status !== undefined && error.status !== status)) return null
  return typeof error.payload === 'object' && error.payload !== null ? (error.payload as Record<string, unknown>) : null
}

function stringField(body: Record<string, unknown>, key: string): string | null {
  return typeof body[key] === 'string' ? (body[key] as string) : null
}

/** Suspension info a 403 carries once an account is suspended (services/api suspension.py's `enforce`,
 * docs/plan/api.md#moderation): every authenticated route refuses it except `POST /export`. [reasonText] is an
 * admin's own wording when one was given for the suspension, else a default; falls back to the generic message. */
export interface Suspension {
  deleteAt: string
  appealEmail: string
  reasonText: string | null
}

export function suspensionOf(error: unknown): Suspension | null {
  const body = errorBody(error, 403)
  if (body && body.suspended === true && typeof body.deleteAt === 'string' && typeof body.appealEmail === 'string') {
    return { deleteAt: body.deleteAt, appealEmail: body.appealEmail, reasonText: stringField(body, 'reasonText') }
  }
  return null
}

/**
 * The `reason`/`reasonText`/`appealEmail` a 429/403 carries past the plain rate limit (docs/plan/api.md#moderation),
 * and [hold] true for a content-review case (#content-holds). Generic across every restricted route (ai, imports,
 * export, offSubmit, ...); excludes a suspended/banned account, shown by the app-wide `SuspendedGate` instead.
 */
export interface Restriction {
  reason: string
  reasonText: string | null
  appealEmail: string | null
  hold: boolean
}

export function restrictionOf(error: unknown): Restriction | null {
  const body = errorBody(error)
  if (!body || typeof body.reason !== 'string' || body.suspended === true) return null
  return { reason: body.reason, reasonText: stringField(body, 'reasonText'), appealEmail: stringField(body, 'appealEmail'), hold: body.hold === true }
}

/**
 * An AI pause in effect, for a screen that knows every error it sees came from an AI route (the three AI screens,
 * via `useAiBlock`): [until] is when a timed strike block lifts itself; null for a pause with no expiry -- an
 * admin block or a content-review hold (docs/plan/api.md#moderation, #content-holds) that only clears when the
 * owner lifts it. [reasonText]/[appealEmail] are the 403/429's own fields, shown instead of generic wording when set.
 */
export interface AiBlock {
  until: string | null
  strikes: number | null
  reasonText: string | null
  appealEmail: string | null
}

export function aiBlockOf(error: unknown): AiBlock | null {
  if (!(error instanceof ApiError)) return null
  const body = errorBody(error)
  if (body && typeof body.blockedUntil === 'string' && typeof body.strikes === 'number') {
    return { until: body.blockedUntil, strikes: body.strikes, reasonText: stringField(body, 'reasonText'), appealEmail: stringField(body, 'appealEmail') }
  }
  // An admin block or a content-review hold: 429/403 with a `reason` but no `blockedUntil` -- no expiry, "until
  // lifted" (docs/plan/api.md#content-holds). Safe to assume "AI" here only because every caller of this function
  // only ever sees errors from an AI route; a generic caller should use [restrictionOf] instead.
  const restriction = restrictionOf(error)
  if (restriction && (error.status === 429 || error.status === 403)) {
    return { until: null, strikes: null, reasonText: restriction.reasonText, appealEmail: restriction.appealEmail }
  }
  return null
}

function retryAfterOf(error: ApiError): number | null {
  const body = errorBody(error, 429)
  return body && typeof body.retryAfter === 'number' && Number.isFinite(body.retryAfter) ? Math.ceil(body.retryAfter) : null
}

/**
 * A calm, people-facing message for anything a screen catches — an API error, a network failure, or anything
 * else — so nothing shows raw JSON, "[object Object]" or a stack trace. Used by `notifyError` (every toast) and
 * by screens that show a message inline instead of a toast (sign-in, delete account, export).
 */
export function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    const body = errorBody(error)
    if (body && typeof body.blockedUntil === 'string' && typeof body.strikes === 'number') {
      const reasonText = stringField(body, 'reasonText')
      const appealEmail = stringField(body, 'appealEmail')
      const base = reasonText ?? `AI is paused until ${formatLocalDateTime(body.blockedUntil)}.`
      return appealEmail ? `${base} Questions? Email ${appealEmail}.` : base
    }
    const restriction = restrictionOf(error)
    if (restriction) {
      const base = restriction.reasonText ?? asSentence(error.message)
      return restriction.appealEmail ? `${base} Questions? Email ${restriction.appealEmail}.` : base
    }
    if (error.status === 429) {
      const retryAfter = retryAfterOf(error)
      return retryAfter !== null ? `${asSentence(error.message)} Try again in ${formatWait(retryAfter)}.` : asSentence(error.message)
    }
    if (error.status === 401) return 'Sign in to continue.'
    if (error.status === 413) return 'That file is too large.'
    if (error.status >= 500) return 'Something went wrong on our side. Try again in a moment.'
    return asSentence(error.message)
  }
  if (error instanceof TypeError) return "You're offline. Check your connection."
  if (error instanceof Error && error.message) return error.message
  return 'Something went wrong. Try again.'
}

/** Talks to the two Lambda function URLs with the signed-in user's access token. */
export class ApiClient {
  private readonly apiUrl: string
  private readonly aiUrl: string
  private readonly tokens: TokenProvider
  private readonly fetchImpl: FetchLike

  constructor(config: Pick<AppConfig, 'apiUrl' | 'aiUrl'>, tokens: TokenProvider, fetchImpl?: FetchLike) {
    this.apiUrl = config.apiUrl
    this.aiUrl = config.aiUrl
    this.tokens = tokens
    this.fetchImpl = fetchImpl ?? ((input, init) => fetch(input, init))
  }

  async pull(options: { since?: number; cursor?: string | null; type?: ItemType } = {}): Promise<PullPage> {
    const query = new URLSearchParams({ since: String(options.since ?? 0) })
    if (options.cursor) query.set('cursor', options.cursor)
    if (options.type) query.set('type', options.type)
    return this.request<PullPage>(this.apiUrl, `/sync/pull?${query}`)
  }

  /** Follows the cursor until every item changed after [since] has been read. */
  async pullAll(options: { since?: number; type?: ItemType } = {}): Promise<{ items: SyncItem[]; since: number }> {
    const items: SyncItem[] = []
    let since = options.since ?? 0
    let cursor: string | null = null
    do {
      const page: PullPage = await this.pull({ since: options.since ?? 0, cursor, type: options.type })
      items.push(...page.items)
      since = Math.max(since, page.since)
      cursor = page.cursor
    } while (cursor)
    return { items, since }
  }

  /** Pushes in batches the API accepts and merges the per-batch results. */
  async push(items: ReadonlyArray<SyncItem<object>>): Promise<PushResult> {
    const result: PushResult = { accepted: [], rejected: [] }
    for (let start = 0; start < items.length; start += MAX_PUSH_ITEMS) {
      const batch = items.slice(start, start + MAX_PUSH_ITEMS)
      const response = await this.request<PushResult>(this.apiUrl, '/sync/push', {
        method: 'POST',
        body: JSON.stringify({ items: batch }),
      })
      result.accepted.push(...response.accepted)
      result.rejected.push(...response.rejected)
    }
    return result
  }

  async searchFoods(text: string, options: { kind?: FoodKind; limit?: number } = {}): Promise<Food[]> {
    // The browser's region ranks its national food table first, as the phone and the AI do (e2e #127).
    const query = new URLSearchParams({ q: text, limit: String(options.limit ?? 20), country: LOCALE.split('-')[1] ?? '' })
    if (options.kind) query.set('kind', options.kind)
    const response = await this.request<{ results: Food[] }>(this.apiUrl, `/foods/search?${query}`)
    return response.results
  }

  async getFood(id: string): Promise<Food> {
    return this.request<Food>(this.apiUrl, `/foods/${encodeURIComponent(id)}`)
  }

  async createUpload(kind: UploadKind, contentType: string): Promise<UploadTicket> {
    return this.request<UploadTicket>(this.apiUrl, '/uploads', {
      method: 'POST',
      body: JSON.stringify({ kind, contentType }),
    })
  }

  /** Uploads a file straight to S3 and returns its key: the pre-signed `post` form when the ticket has one (S3
   * enforces the kind's size limit itself), else the older plain `PUT` (docs/plan/api.md "Uploads"). Photos are
   * redrawn first, so their metadata (GPS location, camera) is never uploaded. */
  async uploadFile(kind: UploadKind, original: Blob): Promise<string> {
    const file = original.type.startsWith('image/') ? await cleanPhoto(original) : original
    const ticket = await this.createUpload(kind, file.type)
    const response = ticket.post ? await this.postUpload(ticket.post, file) : await this.fetchImpl(ticket.url, { method: 'PUT', headers: ticket.headers, body: file })
    if (!response.ok) {
      throw new ApiError(response.status, `upload failed (HTTP ${response.status})`)
    }
    return ticket.key
  }

  /** S3 requires its form fields set before the file, so the file is appended last. */
  private postUpload(post: NonNullable<UploadTicket['post']>, file: Blob): Promise<Response> {
    const body = new FormData()
    for (const [name, value] of Object.entries(post.fields)) body.set(name, value)
    body.set('file', file)
    return this.fetchImpl(post.url, { method: 'POST', body })
  }

  async importRecipeUrl(url: string): Promise<RecipeImport> {
    return this.request<RecipeImport>(this.apiUrl, '/recipes/import-url', { method: 'POST', body: JSON.stringify({ url }) })
  }

  /** The exercise library JSON: its version from the API, then the file from S3 (pre-signed, so no token). */
  async exerciseLibrary(): Promise<{ version: string; library: unknown }> {
    const latest = await this.request<PublishedFile>(this.apiUrl, '/exercises/latest')
    const response = await this.fetchImpl(latest.url)
    if (!response.ok) {
      throw new ApiError(response.status, `exercise library download failed (HTTP ${response.status})`)
    }
    return { version: latest.version, library: await readJsonBody(response) }
  }

  async exportData(): Promise<ExportResult> {
    return this.request<ExportResult>(this.apiUrl, '/export', { method: 'POST', body: '{}' })
  }

  /** The signed-in user's most recent export while it's still downloadable (24 hours); 404 once it has expired.
   * Used by `/export/download`, never called directly from a Settings or suspended-page button. */
  async latestExport(): Promise<LatestExport> {
    return this.request<LatestExport>(this.apiUrl, '/export/latest')
  }

  /** Import from another app (Settings › Data): [payload.key] from `uploadFile('import', file)`. */
  async importData(payload: ImportRequest): Promise<ImportResult> {
    return this.request<ImportResult>(this.apiUrl, '/import', { method: 'POST', body: JSON.stringify(payload) })
  }

  /** Starts the 30-day deletion grace period; signing in again before [deleteAt] cancels it. */
  async deleteAccount(): Promise<DeleteAccountResult> {
    return this.request<DeleteAccountResult>(this.apiUrl, '/account', { method: 'DELETE' })
  }

  /** "Report this result" on an AI screen (settings privacy policy's AI reports). */
  async reportAiResult(payload: AiReportRequest): Promise<void> {
    await this.request(this.aiUrl, '/ai/report', { method: 'POST', body: JSON.stringify(payload) })
  }

  async plate(payload: PlateRequest): Promise<PlateResult> {
    return this.request<PlateResult>(this.aiUrl, '/ai/plate', { method: 'POST', body: JSON.stringify(payload) })
  }

  /** Reads a recipe pasted as text with the recipe prompt (D7); online only, like every AI route. */
  async recipeText(text: string): Promise<AiRecipe> {
    return this.request<AiRecipe>(this.aiUrl, '/ai/recipe-text', { method: 'POST', body: JSON.stringify({ text }) })
  }

  /** Adjusts a generated program from a plain-English request (setup's "Your training" step); online only, like every AI route. */
  async program(payload: ProgramAiRequest): Promise<ProgramAiResult> {
    return this.request<ProgramAiResult>(this.aiUrl, '/ai/program', { method: 'POST', body: JSON.stringify(payload) })
  }

  /** Short-lived signed links to the caller's own uploads (progress photos); at most 60 keys a request. */
  async viewUrls(keys: readonly string[]): Promise<ViewUrls> {
    return this.request<ViewUrls>(this.apiUrl, '/uploads/view', { method: 'POST', body: JSON.stringify({ keys }) })
  }

  /** `GET /config` (public, but sent with a token like every call here): feature switches, maintenance, the
   * version floor and `consentVersion` (docs/plan/api.md "App config"). */
  async getConfig(): Promise<RemoteConfig> {
    return this.request<RemoteConfig>(this.apiUrl, '/config')
  }

  /** `GET /messages`: live in-app messages for this platform/version, plus this account's own with a token. */
  async getMessages(version: string): Promise<InAppMessage[]> {
    const response = await this.request<{ messages: InAppMessage[] }>(this.apiUrl, `/messages?platform=web&version=${encodeURIComponent(version)}`)
    return response.messages
  }

  /** `PUT /push/device`: registers or updates this browser's push subscription (Settings → Notifications). */
  async registerPushDevice(payload: PushDeviceRequest): Promise<void> {
    await this.request(this.apiUrl, '/push/device', { method: 'PUT', body: JSON.stringify(payload) })
  }

  /** `DELETE /push/device/<id>`: forgets the device (notifications turned off, or sign-out). */
  async unregisterPushDevice(installationId: string): Promise<void> {
    await this.request(this.apiUrl, `/push/device/${encodeURIComponent(installationId)}`, { method: 'DELETE' })
  }

  /** Any `/admin/*` route (docs/plan/api.md "Ops (admin) routes"), for the ops panel's `useAdminApi` only —
   * every other caller here goes through its own typed method above. */
  async admin<T>(path: string, init: RequestInit = {}): Promise<T> {
    return this.request<T>(this.apiUrl, path, init)
  }

  /**
   * Sends an authorised request. A 401 gets one refresh and one retry (the token may have expired in
   * flight or been revoked); a second 401 means the session is no longer valid, so it signs out.
   * Bodies are always strings here, so the retry can resend them.
   */
  private async request<T>(base: string, path: string, init: RequestInit = {}): Promise<T> {
    const send = (token: string) => {
      const headers = new Headers(init.headers)
      headers.set('authorization', `Bearer ${token}`)
      headers.set('x-macrotide-platform', PLATFORM)
      headers.set('x-macrotide-version', APP_VERSION)
      if (init.body !== undefined) headers.set('content-type', 'application/json')
      return this.fetchImpl(`${base}${path}`, { ...init, headers })
    }

    const token = await this.tokens.getAccessToken()
    let response = await send(token)
    if (response.status === 401) {
      const fresh = await this.tokens.refreshAccessToken(token)
      response = await send(fresh)
      if (response.status === 401) await this.tokens.signOut()
    }

    const payload = await readJsonBody(response)
    if (!response.ok) {
      const error = new ApiError(response.status, errorMessage(payload, response.status), payload)
      const suspension = suspensionOf(error)
      if (suspension) this.tokens.reportSuspension?.(suspension)
      throw error
    }
    return payload as T
  }
}
